import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getReferralPublicSettings } from "@/lib/referral/referral.functions";

const CANONICAL = "https://fixhen.com/referral/terms";

const settingsQuery = queryOptions({
  queryKey: ["referral", "public-settings"],
  queryFn: () => getReferralPublicSettings(),
  staleTime: 60_000,
});

export const Route = createFileRoute("/referral/terms")({
  loader: ({ context }) => context.queryClient.ensureQueryData(settingsQuery),
  head: () => ({
    meta: [
      { title: "FIXHEN Partner Program Terms & Conditions" },
      {
        name: "description",
        content:
          "Legal terms of the FIXHEN Partner Program: eligibility, tracking, commissions, hold period, payouts, prohibited practices, taxes, termination and liability.",
      },
      { property: "og:title", content: "FIXHEN Partner Program Terms" },
      {
        property: "og:description",
        content: "Full legal terms of the FIXHEN affiliate/referral program.",
      },
      { property: "og:url", content: CANONICAL },
      { name: "robots", content: "index, follow" },
      { property: "og:image", content: "https://fixhen.com/og/home.jpg" },
      { name: "twitter:image", content: "https://fixhen.com/og/home.jpg" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: TermsPage,
});

const bp = (v: number) => (v / 100).toFixed(v % 100 === 0 ? 0 : 1) + "%";
const eur = (cents: number) =>
  new Intl.NumberFormat("en-EU", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(cents / 100);

function TermsPage() {
  const fetchSettings = useServerFn(getReferralPublicSettings);
  const { data: s } = useSuspenseQuery({
    ...settingsQuery,
    queryFn: () => fetchSettings(),
  });

  return (
    <div className="min-h-screen bg-paper text-ink">
      <AnnouncementBar />
      <SiteHeader />

      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <header className="mb-12 border-b border-border pb-8">
          <p className="text-xs font-bold uppercase tracking-widest text-sky-pop">Legal</p>
          <h1 className="mt-2 font-display text-4xl font-black leading-tight sm:text-5xl">
            FIXHEN Partner Program Terms &amp; Conditions
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">
            Version {s.terms_version} · Effective from the date of your acceptance in the application form.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <Link
              to="/referral"
              className="rounded-full border border-border px-4 py-1.5 font-semibold hover:bg-slate-50"
            >
              ← Back to program
            </Link>
            <Link
              to="/referral/apply"
              className="rounded-full bg-ink px-4 py-1.5 font-semibold text-paper hover:opacity-90"
            >
              Apply now
            </Link>
          </div>
        </header>

        <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-black prose-h2:mt-12 prose-h2:text-2xl prose-h3:text-lg prose-h3:mt-8 prose-a:text-sky-pop prose-a:no-underline hover:prose-a:underline prose-strong:text-ink">

          <p className="lead text-lg text-slate-700">
            These Terms &amp; Conditions ("<strong>Terms</strong>") govern your participation
            in the FIXHEN Partner Program (the "<strong>Program</strong>") operated by
            <strong> FIXHEN</strong> ("<strong>we</strong>", "<strong>us</strong>",
            "<strong>our</strong>"). By submitting an application you confirm that you have
            read, understood and agreed to be bound by these Terms.
          </p>

          <h2>1. Definitions</h2>
          <ul>
            <li><strong>Program</strong> — the FIXHEN affiliate/referral program described here.</li>
            <li><strong>Partner</strong>, <strong>you</strong> — the natural person or legal entity approved by us to participate.</li>
            <li><strong>Referral Link / Code</strong> — the unique URL and alphanumeric code we assign you.</li>
            <li><strong>Qualified Sale</strong> — an order placed by an end-customer whose browser carried a valid, unexpired Referral cookie at checkout, that reaches the "shipped" state and is not cancelled, returned or charged back within the Hold Period.</li>
            <li><strong>Commission</strong> — the amount payable to you on a Qualified Sale, calculated per Section 4.</li>
            <li><strong>Hold Period</strong> — {s.hold_days} calendar days from the order's shipping date.</li>
            <li><strong>Cookie Window</strong> — {s.cookie_days} calendar days from the customer's most recent click on your Referral Link.</li>
            <li><strong>Payout</strong> — the transfer of verified Commissions to you per Section 7.</li>
          </ul>

          <h2>2. Eligibility</h2>
          <p>To join the Program you must be at least 18 years old and have full legal capacity to enter into contracts, or represent a duly registered legal entity. Current employees and immediate family members of FIXHEN are not eligible. One account per legal entity. We reserve the right to reject any application at our sole discretion without giving reasons.</p>

          <h2>3. Referral Tracking</h2>
          <p>We track referrals using a first-party cookie (<code>fh_ref</code>) with a <strong>{s.cookie_days}-day</strong> lifetime and <strong>last-click</strong> attribution — if another partner's link is clicked after yours within the window, attribution passes to that partner. The cookie may be blocked, cleared or shortened by the end-customer's browser, extensions, privacy settings, or law; we bear no responsibility for lost attribution due to such factors. Server-to-server or cross-device attribution is <em>not</em> supported.</p>

          <h2>4. Commissions</h2>
          <p>Commission is calculated on the <strong>net order subtotal</strong>, exclusive of VAT, shipping, and any refunded amounts, at the following rates based on your Tier:</p>
          <ul>
            <li><strong>Silver — {bp(s.tier_silver_bp)}</strong> · starting tier.</li>
            <li><strong>Gold — {bp(s.tier_gold_bp)}</strong> · unlocked at {eur(s.gold_threshold_cents)} lifetime verified referred revenue.</li>
            <li><strong>Platinum — {bp(s.tier_platinum_bp)}</strong> · unlocked at {eur(s.platinum_threshold_cents)} lifetime verified referred revenue.</li>
          </ul>
          <p>The applicable rate is <strong>snapshotted at the time of the order</strong>; subsequent tier promotions do not apply retroactively. Tier progress is calculated from verified referred revenue net of returns, chargebacks and reversals.</p>

          <h2>5. Hold Period &amp; Qualification</h2>
          <p>A Commission is considered "pending" from creation until the Hold Period elapses. Only Commissions on Qualified Sales are eligible for Payout. We may reverse an approved Commission at any time if the underlying order is later cancelled, refunded, charged back, disputed, fraudulent, or violates these Terms. Reversals reduce your Tier progress.</p>

          <h2>6. Prohibited Practices</h2>
          <p>You must not, directly or indirectly:</p>
          <ul>
            <li>send unsolicited email, SMS or messaging spam containing your Referral Link;</li>
            <li>bid on "FIXHEN" or any of our trademarks, misspellings or close variants in paid search or on the FIXHEN.com domain;</li>
            <li>engage in cookie stuffing, forced clicks, hidden iframes, or auto-redirects;</li>
            <li>offer cashback, rebates, coupons or other incentives to end-customers without our prior written consent;</li>
            <li>make misleading or unsubstantiated claims about our products, pricing, shipping times or warranties;</li>
            <li>promote the Program on sites containing adult, hateful, illegal, or misleading content;</li>
            <li>self-refer, or arrange for members of your household to purchase through your link;</li>
            <li>register domains, social handles or business names that incorporate "FIXHEN" or our trademarks without written consent.</li>
          </ul>
          <p>Breach of this Section entitles us to withhold or claw back Commissions and terminate the Program agreement without notice.</p>

          <h2>7. Payouts</h2>
          <p>Payouts are made in <strong>EUR</strong> once your verified (post-hold) balance reaches the minimum of <strong>{eur(s.min_payout_cents)}</strong>. A processing fee of <strong>{eur(s.payout_fee_cents)}</strong> per Payout is deducted from the amount payable to cover bank/PSP charges. Supported methods are bank/SEPA transfer, PayPal, Wise and Revolut, subject to availability.</p>
          <p>Payouts are processed within <strong>30 business days</strong> of a valid request. We may delay a Payout to complete required KYC, tax or anti-fraud checks, or if we reasonably suspect a violation of these Terms.</p>

          <h2>8. Taxes &amp; Invoicing</h2>
          <p>You are solely responsible for declaring and paying all taxes, duties and social contributions on Commissions received in your jurisdiction. If you are a VAT-registered business established in the EU, you agree to issue a valid VAT invoice to FIXHEN or to accept a self-billing arrangement where offered; where the reverse-charge mechanism applies it will be indicated on the invoice. If you are a natural person we may require you to provide a tax identification number for reporting purposes.</p>

          <h2>9. Data Protection</h2>
          <p>We process your personal data (application details, payout information, activity metrics) for the purposes of operating the Program, on the legal basis of the contract between us (Art. 6(1)(b) GDPR) and our legal obligations (Art. 6(1)(c) GDPR). You may exercise your rights of access, rectification, erasure, restriction, portability and objection by contacting <a href="mailto:info@contact.fixhen.com">info@contact.fixhen.com</a>. We do <strong>not</strong> share personal data of end-customers with you; you receive only aggregated performance metrics.</p>

          <h2>10. Intellectual Property &amp; Marketing Materials</h2>
          <p>Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to use our name, logo and approved marketing creatives solely for the purpose of promoting FIXHEN under the Program. You must not modify our logo, misrepresent the relationship between us, or imply endorsement of any third party. All goodwill arising from your use inures to us.</p>

          <h2>11. Term &amp; Termination</h2>
          <p>The Program agreement starts on approval of your application and continues until terminated by either party. Either party may terminate for convenience on <strong>14 calendar days</strong> written notice. We may terminate immediately for breach, fraud, insolvency, or reputational risk. Upon termination:</p>
          <ul>
            <li>your Referral Link is disabled;</li>
            <li>verified Commissions above the Payout minimum will be paid in the next scheduled Payout cycle;</li>
            <li>pending (unverified) Commissions are forfeited;</li>
            <li>any amounts owed to us due to reversals or breach remain due and payable.</li>
          </ul>

          <h2>12. Changes to the Program</h2>
          <p>We may change these Terms, the Commission rates, Tier thresholds, Hold Period, Cookie Window, Payout minimum, processing fee or any other operational parameter, on <strong>30 days' notice</strong> by email or in-dashboard notice. Continued participation after the effective date constitutes acceptance. Rate changes are not applied retroactively to already-recorded orders.</p>

          <h2>13. Warranties &amp; Liability</h2>
          <p>The Program is provided "as is". To the maximum extent permitted by law we disclaim all implied warranties. To the maximum extent permitted by law our aggregate liability arising out of or relating to the Program is limited to the total Commissions actually paid to you in the twelve (12) months preceding the event giving rise to the claim. Nothing in these Terms excludes or limits any liability that cannot be excluded or limited under mandatory law.</p>

          <h2>14. Indemnity</h2>
          <p>You will indemnify, defend and hold harmless FIXHEN, its officers, employees and agents from any third-party claim arising out of your participation in the Program, your breach of these Terms, your marketing content, your use of our trademarks, or your violation of any applicable law.</p>

          <h2>15. Independent Contractors</h2>
          <p>Nothing in these Terms creates an employment, agency, partnership or joint venture relationship. You are an independent contractor and are solely responsible for your own operations, personnel and expenses.</p>

          <h2>16. Governing Law &amp; Jurisdiction</h2>
          <p>These Terms are governed by the laws of the <strong>Republic of Bulgaria</strong>, excluding its conflict of laws rules. The exclusive jurisdiction for any dispute is the competent courts of the City of Sofia, Bulgaria, without prejudice to any mandatory consumer protection rights. Consumers in the EU may also use the <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">Online Dispute Resolution platform</a>.</p>

          <h2>17. Miscellaneous</h2>
          <p>If any provision of these Terms is held unenforceable, the remainder remains in effect. Our failure to enforce any right is not a waiver. You may not assign these Terms without our written consent; we may assign them freely as part of a corporate reorganization. These Terms, together with your accepted application, constitute the entire agreement between us regarding the Program and supersede any prior understanding.</p>

          <h2>18. Contact</h2>
          <p>
            For all Program and privacy enquiries, please use the same contact channel as
            the rest of the site:<br />
            Email: <a href="mailto:info@contact.fixhen.com">info@contact.fixhen.com</a><br />
            Phone: <a href="tel:+359887752171">+359 88 775 2171</a><br />
            Address: 6 Prof. Dr Dimitar Dobrev St., Sofia, Bulgaria<br />
            Hours: Mon–Fri, 09:00–18:00 EET
          </p>



        </div>
      </article>

      <SiteFooter />
    </div>
  );
}
